/**
 * RECORDING THE CUSTOMER'S YES — the first thing this build can WRITE.
 *
 * It is first on purpose. Authorization is the one the last system got
 * wrong in both directions at once: it demanded a signature image that a
 * phone call can never produce, so staff were asked four times for
 * something the screen could not give; and it let the portal write a fresh
 * row on every tap, so one job collected eight identical signatures.
 *
 * Both faults are designed out here rather than guarded against:
 *
 *   - What is REQUIRED is who agreed and when. B&P 9884.9 asks for the
 *     customer's authorization against a written estimate, not for ink. A
 *     signature is proof on top, never the thing itself.
 *   - The unique index on (job_id, kind, amount) means the same figure
 *     cannot be authorized twice. A REVISED figure still can, because that
 *     is a different agreement about a different number.
 *
 * The insert goes through the caller's own session, so row level security
 * decides whether they may. There is no service key anywhere near this.
 */

import { db } from "./client.ts";
import { missingDisclosures } from "../domain/teardown.ts";

export type NewAuthorization = {
  jobId: string;
  amount: number;
  authorizedBy: string;
  method: "phone" | "in_person" | "counter" | "portal";
  kind?: "initial" | "additional" | "revised";
  /**
   * A yes to a garage door shop's free estimate visit, which has no figure
   * by design (src/domain/visitquote.ts). The one case where $0 is the
   * right amount rather than a figure somebody forgot to type. Set only by
   * the job card, and only on a job that is the visit line and nothing
   * else.
   */
  freeVisit?: boolean;
  relationship?: string;
  phoneUsed?: string;
  declined?: boolean;
  declineReason?: string;
  /**
   * B&P 9884.10. Undefined means nobody was asked, which is stored as
   * null and is NOT the same as no. See src/domain/partsreturn.ts.
   */
  partsReturnWanted?: boolean;
  /**
   * What this yes covers. Defaults to repair, which is what the desk
   * form records. Towing needs its own, separately. See 0119, and 0127
   * for the teardown.
   */
  authorizes?: "repair" | "storage" | "tow" | "teardown";
  /**
   * THE TEARDOWN DISCLOSURE. Only on authorizes: "teardown", and
   * required there unless the customer declined.
   *
   * Unlike everything else in this file these are not optional extras on
   * a record of something that happened -- they ARE the thing being
   * recorded. An agreement to disassembly that does not say what
   * reassembly costs or how long it takes is a signature on a blank. The
   * database refuses one (0127) and so does this function, so the person
   * gets a sentence rather than a constraint name.
   */
  teardown?: {
    reassemblyCost: number | null;
    destroyedCost: number | null;
    restorable: boolean | null;
    restoreNote?: string;
    maxDays: number | null;
  };
};

export async function recordAuthorization(a: NewAuthorization): Promise<void> {
  const name = a.authorizedBy.trim();
  if (!name) throw new Error("Record who gave the authorization — a name is the whole point.");
  if (!a.declined && !(a.amount > 0) && !a.freeVisit) throw new Error("Enter the figure they agreed to.");
  if (a.declined && !(a.declineReason ?? "").trim()) {
    throw new Error("A refusal needs a reason, or it is a lost job nobody can learn from.");
  }

  /* THE TEARDOWN DISCLOSURE IS CHECKED BEFORE THE INSERT, NOT AFTER.
     0127 enforces it in the database and that is what makes it true of
     the data forever. This is what makes the screen say which box is
     empty instead of showing somebody "job_auth_teardown_is_disclosed".
     Both, on purpose. A declined teardown is exempt in both places. */
  if (a.authorizes === "teardown" && !a.declined) {
    const missing = missingDisclosures({
      disassembly: a.amount,
      reassembly: a.teardown?.reassemblyCost ?? null,
      destroyed: a.teardown?.destroyedCost ?? null,
      restorable: a.teardown?.restorable ?? null,
      restoreNote: (a.teardown?.restoreNote ?? "").trim() || null,
      maxDays: a.teardown?.maxDays ?? null,
    });
    if (missing.length) {
      throw new Error(
        "Before they can agree to a teardown they have to be told "
        + `${missing.join("; ")}. Nothing has been saved.`,
      );
    }
  }

  const sb = await db();
  const { data: profile } = await sb.auth.getUser();

  // account_id is NOT defaulted here. The column has a default that reads
  // the caller's session, and relying on a default for the column every
  // security policy is anchored on is how rows end up belonging to nobody.
  const { data: me } = await sb.from("profiles").select("account_id").eq("id", profile.user?.id ?? "").maybeSingle();
  if (!me) throw new Error("Your login is not attached to a shop.");

  const { error } = await sb.from("job_authorizations").insert({
    account_id: (me as { account_id: string }).account_id,
    job_id: a.jobId,
    kind: a.kind ?? "initial",
    method: a.method,
    amount: a.declined ? 0 : a.amount,
    authorized_by: name,
    relationship: a.relationship?.trim() || null,
    phone_used: a.phoneUsed?.trim() || null,
    declined: !!a.declined,
    decline_reason: a.declined ? (a.declineReason ?? "").trim() : null,
    parts_return_wanted: a.partsReturnWanted ?? null,
    authorizes: a.authorizes ?? "repair",
    /* Only ever written on a teardown. 0127 has a check that refuses a
       repair row carrying a reassembly cost, because that is a data
       entry accident that would print on a document about something
       else. Sending nulls here keeps every other path exactly as it was. */
    teardown_reassembly_cost: a.authorizes === "teardown" ? a.teardown?.reassemblyCost ?? null : null,
    teardown_destroyed_cost: a.authorizes === "teardown" ? a.teardown?.destroyedCost ?? null : null,
    teardown_restorable: a.authorizes === "teardown" ? a.teardown?.restorable ?? null : null,
    teardown_restore_note: a.authorizes === "teardown"
      ? (a.teardown?.restoreNote ?? "").trim() || null
      : null,
    teardown_max_days: a.authorizes === "teardown" ? a.teardown?.maxDays ?? null : null,
    recorded_by: profile.user?.id ?? null,
  });

  if (error) {
    // 23505 is the unique index doing its job. Said in words, because
    // "duplicate key value violates unique constraint" is not an answer.
    if (error.code === "23505") {
      throw new Error("That exact figure is already authorized on this job. If the price changed, record the new figure instead.");
    }
    throw new Error(error.message);
  }
}

export type AuthRow = {
  id: string; job_id: string; kind: string; method: string; amount: number;
  authorized_by: string; signature: string | null; declined: boolean;
  decline_reason: string | null; authorized_at: string;
  jobs: { job_number: string; customers: { full_name: string } | null } | null;
};

export async function listAuthorizations(limit = 200): Promise<AuthRow[]> {
  const sb = await db();
  const { data, error } = await sb.from("job_authorizations")
    .select("id,job_id,kind,method,amount,authorized_by,signature,declined,decline_reason,authorized_at," +
            "jobs(job_number,customers(full_name))")
    .order("authorized_at", { ascending: false }).limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as AuthRow[];
}
